import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types/user';
import { api } from './api';
import i18n from './i18n';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  oauthError: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, email?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearOAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Sync user's preferred language with i18n
  const syncLanguage = (userData: User) => {
    if (userData.preferred_language) {
      i18n.changeLanguage(userData.preferred_language);
      localStorage.setItem('i18nextLng', userData.preferred_language);
    }
  };

  // Check if user is authenticated on mount
  useEffect(() => {
    // Check for OAuth errors in URL
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('oauth_error');
    if (errorParam) {
      setOauthError(decodeURIComponent(errorParam));
      // Remove the error from URL without reloading
      window.history.replaceState({}, '', window.location.pathname);
    }

    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get<User>('/api/auth/me');
      setUser(response);
      syncLanguage(response);
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    const response = await api.post<{ data: User }>('/api/auth/login', {
      username,
      password,
    });
    setUser(response.data);
    syncLanguage(response.data);
  };

  const register = async (username: string, password: string, email?: string) => {
    const response = await api.post<{ data: User }>('/api/auth/register', {
      username,
      password,
      email,
    });
    setUser(response.data);
    syncLanguage(response.data);
  };

  const logout = async () => {
    await api.post('/api/auth/logout', {});
    setUser(null);
  };

  const clearOAuthError = () => {
    setOauthError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        oauthError,
        login,
        register,
        logout,
        clearOAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
